import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Users, Target } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { MONETISEUP_LOGO, EXTERNAL } from "@/constants/brand";

const solutions = [
  {
    id: "publishers",
    label: "For Publishers",
    desc: "Maximize fill rate & eCPM",
    href: "/#publishers",
    icon: Users,
  },
  {
    id: "advertisers",
    label: "For Advertisers",
    desc: "Reach the right audience",
    href: "/#advertisers",
    icon: Target,
  },
];

const navItems = [
  { id: "services", label: "Services", href: "/#services" },
  { id: "manga", label: "Manga", href: "/manga" },
  { id: "demo", label: "Demo", href: "/demo" },
  { id: "about", label: "About", href: "/#about" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setSolutionsOpen(false);
  }, [pathname]);

  const isInternal = (href) => href.startsWith("/") && !href.includes("#");

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "py-3 bg-[#050B26]/70 backdrop-blur-xl border-b border-white/10"
          : "py-4"
      }`}
    >
      <div className="container-x flex items-center justify-between">
        <Link
          to="/"
          data-testid={NAV.logo}
          className="flex items-center group"
        >
          <img
            src={MONETISEUP_LOGO}
            alt="MonetiseUP"
            className="h-9 md:h-10 w-auto"
            style={{ filter: "drop-shadow(0 0 18px rgba(0,240,255,0.25))" }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {/* Solutions dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setSolutionsOpen(true)}
            onMouseLeave={() => setSolutionsOpen(false)}
          >
            <button
              data-testid={NAV.link("solutions")}
              className="inline-flex items-center gap-1 px-3.5 py-2 text-sm text-white/70 hover:text-white rounded-full transition-colors hover:bg-white/5"
            >
              Solutions
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${
                  solutionsOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {solutionsOpen && (
              <div
                data-testid="nav-solutions-dropdown"
                className="absolute top-full left-1/2 -translate-x-1/2 pt-3"
              >
                <div className="glass-strong rounded-2xl p-2 min-w-[320px] shadow-2xl">
                  {solutions.map((s) => (
                    <a
                      key={s.id}
                      href={s.href}
                      data-testid={NAV.link(s.id)}
                      className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 group transition-colors"
                    >
                      <div className="shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-[#0047FF]/30 to-[#00F0FF]/20 border border-white/10 grid place-items-center">
                        <s.icon className="w-4 h-4 text-[#00F0FF]" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white group-hover:text-[#00F0FF] transition-colors">
                          {s.label}
                        </div>
                        <div className="text-xs text-white/55 mt-0.5">
                          {s.desc}
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {navItems.map((item) =>
            isInternal(item.href) ? (
              <Link
                key={item.id}
                to={item.href}
                data-testid={NAV.link(item.id)}
                className="px-3.5 py-2 text-sm text-white/70 hover:text-white rounded-full transition-colors hover:bg-white/5"
              >
                {item.label}
              </Link>
            ) : (
              <a
                key={item.id}
                href={item.href}
                data-testid={NAV.link(item.id)}
                className="px-3.5 py-2 text-sm text-white/70 hover:text-white rounded-full transition-colors hover:bg-white/5"
              >
                {item.label}
              </a>
            ),
          )}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={EXTERNAL.dashboard}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-signin"
            className="hidden md:inline text-sm text-white/70 hover:text-white"
          >
            Sign in
          </a>
          <Link
            to={EXTERNAL.signup}
            data-testid={NAV.ctaSignup}
            className="btn-primary text-sm"
          >
            Start Monetizing
          </Link>
          <button
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden ml-1 p-2 rounded-md hover:bg-white/5"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden mt-3 container-x">
          <div className="glass-strong rounded-2xl p-4 flex flex-col gap-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 px-4 pt-2 pb-1">
              Solutions
            </div>
            {solutions.map((s) => (
              <a
                key={s.id}
                href={s.href}
                data-testid={`mobile-${NAV.link(s.id)}`}
                className="px-4 py-3 rounded-lg hover:bg-white/5 text-white/80"
              >
                {s.label}
              </a>
            ))}
            <div className="border-t border-white/5 my-2" />
            {navItems.map((item) =>
              isInternal(item.href) ? (
                <Link
                  key={item.id}
                  to={item.href}
                  data-testid={`mobile-${NAV.link(item.id)}`}
                  className="px-4 py-3 rounded-lg hover:bg-white/5 text-white/80"
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.id}
                  href={item.href}
                  data-testid={`mobile-${NAV.link(item.id)}`}
                  className="px-4 py-3 rounded-lg hover:bg-white/5 text-white/80"
                >
                  {item.label}
                </a>
              ),
            )}
            <a
              href={EXTERNAL.dashboard}
              target="_blank"
              rel="noreferrer"
              className="px-4 py-3 rounded-lg hover:bg-white/5 text-white/80"
            >
              Sign in
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
